/**
 * Pure URL/state helpers shared by the browse SSR hook and the browse UI.
 *
 * The server and the browser must agree on three derivations or they render
 * different pages: which preset is active, which query-string key carries the
 * page number, and which page we are on. Keeping all three in one module is
 * what makes that agreement enforceable rather than hopeful.
 *
 * `URLSearchParams` rather than `query-string`: the addon declares no runtime
 * dependencies, and core's own use of `qs` is an implementation detail we
 * should not borrow through a hoisted node_modules layout.
 */
import { findBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import { slugify } from '@plone/volto/helpers/Utils/Utils';

/** Block types core's usePagination treats as paginated. */
const PAGINATED_BLOCKS = ['search', 'listing'];

export const presetIdFrom = (search) =>
  new URLSearchParams(search || '').get('preset') || null;

/**
 * Which query-string key carries this block's page number.
 *
 * Mirrors core `useCreatePageQueryStringKey` (helpers/Utils/usePagination)
 * exactly: `page`, unless the page carries more than one paginated block, in
 * which case each block gets `page-<slugified block id>`. Core's own
 * `getListingBlockAsyncData` instead infers the key from the NUMBER of query
 * params, which silently yields `undefined` on `?preset=x&page=2`; deriving it
 * from the same input the client uses is the only form that agrees on every URL.
 */
export const pageKeyFor = (content, id) =>
  findBlocks(content?.blocks || {}, PAGINATED_BLOCKS).length > 1
    ? slugify(`page-${id}`)
    : 'page';

/**
 * The free-text term a URL asks for, read the way core's `withSearch` reads it:
 * the bare `SearchableText` param first, then the `SearchableText` row of the
 * serialized `query` param the block writes for itself. Empty means no term.
 *
 * The server hook needs it so a `/documents?SearchableText=` link renders the
 * results it names rather than the unfiltered hub (BL-0143).
 */
export const searchTextFrom = (search) => {
  const params = new URLSearchParams(search || '');
  const bare = (params.get('SearchableText') || '').trim();
  if (bare) return bare;
  try {
    const rows = JSON.parse(params.get('query') || '[]');
    const row = Array.isArray(rows)
      ? rows.find((r) => r?.i === 'SearchableText')
      : null;
    return typeof row?.v === 'string' ? row.v.trim() : '';
  } catch {
    return '';
  }
};

/**
 * The query criterion for that term, in the shape core's `normalizeState`
 * builds on the client, or null when the URL carries no term.
 */
export const searchTextCriterion = (search) => {
  const text = searchTextFrom(search);
  return text
    ? {
        i: 'SearchableText',
        o: 'plone.app.querystring.operation.string.contains',
        v: text,
      }
    : null;
};

export const pageFrom = (search, key) => {
  const n = parseInt(new URLSearchParams(search || '').get(key), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

/**
 * The same URL at a different page. Page 1 drops the param entirely so the
 * first page has exactly ONE address — otherwise `?page=1` and the bare URL
 * would be two crawlable copies of the same result set.
 */
export const pageHref = (pathname, search, key, page) => {
  const params = new URLSearchParams(search || '');
  if (page > 1) params.set(key, String(page));
  else params.delete(key);
  const q = params.toString();
  return q ? `${pathname}?${q}` : pathname;
};

/** Site-relative href for a preset (or the unfiltered container for null). */
export const presetHref = (pathname, presetId) =>
  presetId ? `${pathname}?preset=${encodeURIComponent(presetId)}` : pathname;

/**
 * A facet value as one comparable thing: null when empty, the value itself
 * when single, the array when there are several. `'x'` and `['x']` are the
 * same selection: core seeds a `.any` base row as an array and a single
 * select reports a string.
 */
export const singleFacetValue = (v) => {
  if (Array.isArray(v)) {
    if (v.length === 0) return null;
    return v.length === 1 ? v[0] : v;
  }
  return v === undefined || v === null || v === '' ? null : v;
};

/** The `{ index, value }` a routed preset stands for, or null. */
export const routedPresetValue = (routes = {}, presetId = null) => {
  if (!presetId) return null;
  for (const index of Object.keys(routes)) {
    for (const value of Object.keys(routes[index])) {
      if (routes[index][value] === presetId) return { index, value };
    }
  }
  return null;
};

/**
 * Where a facet change takes the visitor: `{ index, presetId }`, with
 * `presetId: null` meaning the unfiltered container, or null to filter in
 * place as usual.
 *
 * Only a change on a ROUTED index navigates (presets.ts `facetPresetRoutes`):
 * picking a value that has its own page goes to that page, and clearing it on
 * that page goes back to the container. It routes only from the container or
 * from another routed page. On a curated preset ("Recently Canceled") the same
 * pick narrows in place: there it refines the page the visitor chose, and
 * navigating would widen results they asked to narrow.
 *
 * `current` can lack the routed value on a routed page (state seeded from the
 * URL alone), so the active preset's own value stands in for it.
 */
export const facetRouteTarget = ({
  routes = {},
  activePreset = null,
  current = {},
  next = {},
}) => {
  const active = routedPresetValue(routes, activePreset);
  if (activePreset && !active) return null;
  for (const index of Object.keys(routes)) {
    const was =
      singleFacetValue(current?.[index]) ??
      (active?.index === index ? active.value : null);
    const now = singleFacetValue(next?.[index]);
    if (JSON.stringify(was) === JSON.stringify(now)) continue;
    if (now === null) {
      return active?.index === index ? { index, presetId: null } : null;
    }
    return typeof now === 'string' && routes[index][now]
      ? { index, presetId: routes[index][now] }
      : null;
  }
  return null;
};

const PAQO = 'plone.app.querystring.operation';

/**
 * The URL a facet-driven preset switch goes to.
 *
 * `rows` are the visitor's other facet selections, as query rows; they
 * replace every existing `query` row on a `dropIndexes` index. Rows on other
 * indexes (the free-text term) and the bare `SearchableText` param stay.
 * Everything else goes: a new result set starts at page 1, and a fresh load
 * of this URL takes sort and page size from the target page, so the URL says
 * only what that load reads. Operations are written in core's short `paqo`
 * form, as withSearch serializes them.
 */
export const presetSwitchHref = ({
  pathname,
  search,
  presetId = null,
  dropIndexes = [],
  rows = [],
}) => {
  const params = new URLSearchParams(search || '');
  let existing = [];
  try {
    const parsed = JSON.parse(params.get('query') || '[]');
    if (Array.isArray(parsed)) existing = parsed;
  } catch (e) {
    existing = [];
  }
  const query = [
    ...existing.filter((row) => row && !dropIndexes.includes(row.i)),
    ...rows.map((row) => ({
      ...row,
      o: String(row.o || '').replace(PAQO, 'paqo'),
    })),
  ];
  const out = [];
  if (presetId) out.push(['preset', presetId]);
  if (query.length) out.push(['query', JSON.stringify(query)]);
  const text = params.get('SearchableText');
  if (text) out.push(['SearchableText', text]);
  const q = out.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return q ? `${pathname}?${q}` : pathname;
};

/** Absolute URL from a site config + a site-relative path. */
export const absoluteUrl = (site, relative) => {
  const origin = (site?.baseUrl || '').replace(/\/+$/, '');
  if (!origin) return relative;
  return `${origin}${relative.startsWith('/') ? '' : '/'}${relative}`;
};

/**
 * The window of page numbers to link. Linking all of them would be 295 links
 * on the directives container; first + last + a neighbourhood keeps the page
 * readable while leaving every page reachable in a few hops.
 */
export const pageWindow = (current, total, radius = 2) => {
  const wanted = new Set([1, total]);
  for (let p = current - radius; p <= current + radius; p += 1) {
    if (p >= 1 && p <= total) wanted.add(p);
  }
  return Array.from(wanted).sort((a, b) => a - b);
};
